/**
 * Landing photography — edit paths in {@link LANDING_IMAGES}.
 *
 * Local files: put images under `public/images/landing/` and reference them as
 * `/images/landing/your-file.jpg` (Next serves `public/` at the site root).
 * Remote: full `https://…` URLs still work (e.g. Unsplash with crop params).
 */

/** Files in `public/images/landing/` — portrait phones, wide `md+`; AVIF with WebP fallback. */
const LANDING_HERO_FILES = {
  mobile: {
    avif: "/images/landing/landing-hero-mobile-2000x2500.avif",
    webp: "/images/landing/landing-hero-mobile-2000x2500.webp",
  },
  desktop: {
    avif: "/images/landing/landing-hero-desktop-3840x2160.avif",
    webp: "/images/landing/landing-hero-desktop-3840x2160.webp",
  },
} as const

/** Responsive hero: portrait crop phones, wide crop `md+`. */
export const LANDING_HERO_IMAGE_SOURCES = LANDING_HERO_FILES

/** Demo card + memory strip — desktop WebP at full hero width. */
export const LANDING_HERO_FALLBACK_SRC = LANDING_HERO_IMAGE_SOURCES.desktop.webp

/** `<picture><img src>` — mobile WebP for narrow default + no-source fallback. */
export const LANDING_HERO_PICTURE_IMG_SRC = LANDING_HERO_IMAGE_SOURCES.mobile.webp

/** Hero + three gallery strip / grid tiles — local paths and/or remote URLs. */
export const LANDING_IMAGES = {
  hero: LANDING_HERO_FALLBACK_SRC,
  /** Gallery — max one detail-forward slot (hands + bowl; ceremony context, people visible). */
  gallery1:
    "https://images.unsplash.com/photo-1630861412757-d9743d318312?auto=format&fit=crop&w=560&h=448&q=70",
  /** Gallery — small group around a table, talking / collaborating. */
  gallery2:
    "https://images.unsplash.com/photo-1600880292203-757bb62b4baf?auto=format&fit=crop&w=560&h=448&q=70",
  /** Gallery — busy dining room, multiple guests mid-moment. */
  gallery3:
    "https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?auto=format&fit=crop&w=560&h=448&q=70",
} as const

export type LandingImageKey = keyof typeof LANDING_IMAGES

/**
 * Remote URLs: append Unsplash-style `w` / `h` query params.
 * Local app assets (`/images/…`): return unchanged — use a file that is already the size you need.
 */
export function landingMediaImageCrop(url: string, w: number, h: number): string {
  if (url.startsWith("/")) {
    return url
  }
  const u = new URL(url)
  u.searchParams.set("w", String(w))
  u.searchParams.set("h", String(h))
  return u.toString()
}

/** Darkens the top band so the floating header + logo stay legible on bright hero frames. */
export const LANDING_HERO_HEADER_BLEND_STYLE = {
  position: "absolute" as const,
  inset: 0,
  zIndex: 1,
  pointerEvents: "none" as const,
  background:
    "linear-gradient(to bottom, oklch(0% 0 0 / 0.55) 0%, oklch(0% 0 0 / 0.28) 45%, transparent 100%)",
}

/**
 * Primary hero scrim:
 * - dark pass keeps headline/body readable
 * - background pass blends the image into the page surface (light/dark) to avoid hard cut lines
 */
export const LANDING_HERO_IMAGE_OVERLAY_STYLE = {
  position: "absolute" as const,
  inset: 0,
  zIndex: 2,
  pointerEvents: "none" as const,
  background:
    [
      "linear-gradient(to top, oklch(0% 0 0 / 0.84) 0%, oklch(0% 0 0 / 0.72) 24%, oklch(0% 0 0 / 0.48) 50%, oklch(0% 0 0 / 0.22) 74%, transparent 100%)",
      "linear-gradient(to top, var(--background) 0%, color-mix(in oklab, var(--background) 72%, transparent) 24%, transparent 54%)",
    ].join(", "),
}

/**
 * Memory strip + preview grid — {@link LANDING_IMAGES.gallery1}–{@link LANDING_IMAGES.gallery3}
 * plus hero: at least two clear multi-person interaction frames; at most one detail-first tile.
 */
export const LANDING_MEMORY_STRIP = [
  {
    src: LANDING_IMAGES.gallery1,
    alt: "Hands and bowl during a coffee ceremony moment",
  },
  {
    src: LANDING_IMAGES.gallery2,
    alt: "Colleagues talking together around a table in daylight",
  },
  {
    src: LANDING_IMAGES.gallery3,
    alt: "Guests and staff in a lively restaurant dining room",
  },
  {
    src: LANDING_IMAGES.hero,
    alt: "Hero photograph for the landing page",
  },
] as const
